import {
  COMPOSITION_GET_DATA_V2,
  type CompositionV2DataQuery,
  METRIC_MODE_IDS,
  POSTING_GET_DATA,
  POSTING_SOURCE_IDS,
  type PostingsDataQuery,
} from '@revelio/data-access';

import { useMonthApiFilters } from '../../../../entity-summary/use-month-api-filters';
import { useAvailableDateRangeWithTrialLag } from '../../../filters/hooks/useAvailableDateRangeWithTrialLag';
import { useEffectiveFilters } from '../../../filters/hooks/useEffectiveFilters';
import type { PrimaryFilterId } from '../../../filters/types/filters';
import { hasPrimaryFilter } from '../../../filters/utils/common';
import {
  getFormattedDateString,
  utcToLocal,
} from '../../../filters/utils/dateUtils';
import { getCompositionV2Filters } from '../../../filters/utils/getCompositionV2Filters';
import { getDim1 } from '../../../filters/utils/getDim1';
import { getPostingFilters } from '../../../filters/utils/getPostingFilters';
import { useUrqlQueryWithStatus } from '../utils/data-provider-results-utils';
import { combineTwoQueryStatuses } from './market-condition-metrics-utils';

export function useCompositionAndPostingsQueries(
  primaryDimension: PrimaryFilterId
) {
  const compositionEffectiveFilters = useEffectiveFilters({
    viewGranularity: 'month',
  });
  const dateRangeApiFilters = useMonthApiFilters();
  const compositionVariables = getCompositionV2Filters({
    primaryDimension,
    effectiveFilters: compositionEffectiveFilters,
    isSnapshot: false,
  });

  const { availableDateRange } = useAvailableDateRangeWithTrialLag();
  const availableStartDate = utcToLocal(availableDateRange?.month.min);
  const availableEndDate = utcToLocal(availableDateRange?.month.max);
  const endDate =
    compositionVariables.filters.end_date ??
    getFormattedDateString(availableEndDate, 'month');
  const startDate =
    compositionVariables.filters.start_date ??
    getFormattedDateString(availableStartDate, 'month');

  const compositionPaused =
    !compositionVariables.entities ||
    (Array.isArray(compositionVariables.entities) &&
      compositionVariables.entities.length === 0) ||
    !startDate ||
    !endDate;

  const [{ data: compositionData, status: compositionStatus }] =
    useUrqlQueryWithStatus<CompositionV2DataQuery>({
      query: COMPOSITION_GET_DATA_V2,
      variables: compositionVariables,
      pause: compositionPaused,
    });

  const dim1 = getDim1(primaryDimension);
  const postingEffectiveFilters = useEffectiveFilters({
    viewGranularity: 'week',
  });
  const postingFilters = getPostingFilters(
    primaryDimension,
    postingEffectiveFilters
  );
  const isPrimaryFilterSet = hasPrimaryFilter({
    primaryDimension,
    filters: compositionEffectiveFilters,
  });

  const postingsPaused = !isPrimaryFilterSet || !dateRangeApiFilters;

  const [{ data: postingsData, status: postingsStatus }] =
    useUrqlQueryWithStatus<PostingsDataQuery>({
      query: POSTING_GET_DATA,
      variables: {
        dim1,
        filters: {
          ...postingFilters,
          provider: [POSTING_SOURCE_IDS.unified],
          metric_mode: METRIC_MODE_IDS.expectedHires,
        },
        ...(localStorage.getItem('skip_share_of_postings_scaling') === 'true'
          ? { skip_share_of_postings_scaling: true }
          : {}),
      },
      pause: postingsPaused,
    });

  const status = combineTwoQueryStatuses(compositionStatus, postingsStatus);

  return {
    compositionData: compositionData?.composition_v2,
    postingsData: postingsData?.posting,
    status,
  };
}
